import { useMemo, useRef, useState } from 'react';

import { ResponsiveLoader, WidePageWrapper } from 'components';
import { AddAllessehCollection } from 'components/context-menu-actions/add-allesseh-collection/AddAllessehCollection';
import { AddCollection } from 'components/context-menu-actions/add-collection/AddCollection';
import { AddLinkedItem, AddMobileLinkedItem } from 'components/context-menu-actions/add-linked-item/AddLinkedItem';
import { AddQuery } from 'components/context-menu-actions/add-query/AddQuery';
import { ConvertToCollection } from 'components/context-menu-actions/convert-to-collection/ConvertToCollection';
import { CopyPublishedURL } from 'components/context-menu-actions/copy-published-url/CopyPublishedURL';
import { DetachFromCollection } from 'components/context-menu-actions/detach-from-collection/DetachFromCollection';
import { EditAltSumm } from 'components/context-menu-actions/edit-alt-summ/EditAltSumm';
import { EditCollection } from 'components/context-menu-actions/edit-collection/EditCollection';
import { EditLinkedItem } from 'components/context-menu-actions/edit-linked-item/EditLinkedItem';
import { EditModule } from 'components/context-menu-actions/edit-module/EditModule';
import { EditQuery } from 'components/context-menu-actions/edit-query/EditQuery';
import { ExcludeFromQuery } from 'components/context-menu-actions/exclude-from-query/ExcludeFromQuery';
import ExternalLink, { ExternalLinkContentType } from 'components/context-menu-actions/external-link/ExternalLink';
import MobileArticleActions from 'components/context-menu-actions/mobile-card-options/MobileArticleActions';
import { OpenInCMS } from 'components/context-menu-actions/open-in-cms/OpenInCMS';
import { OpenInNewsGrid } from 'components/context-menu-actions/open-in-newsgrid/OpenInNewsGrid';
import { RemoveEntity } from 'components/context-menu-actions/remove-entity/RemoveEntity';
import { ViewPublishedURL } from 'components/context-menu-actions/view-published-url/ViewPublishedURL';
import { PageRootContainer } from 'components/page-root-container/PageRootContainer';
import {
  ContextMenuActionsProvider,
  isAllowedInZone,
  MenuActions,
  MenuActionsKeys,
  showIfMobileArticle
} from 'contexts/context-menu-actions/ContextMenuActionsContext';
import { usePageLock } from 'contexts/page-lock/usePageLock';
import { usePagePublish } from 'contexts/page-publish-dto/usePagePublish';
import { usePageTypeSettingsContext } from 'contexts/page-type-settings/usePageTypeSettings';
import {
  ArticleItem,
  ExternalCollectionItem,
  LinkedItem,
  MobileArticleItem,
  ModuleContainer,
  ModuleContainerUnion,
  PageModule,
  QueryItem,
  UiModuleType,
  UiTitleTreatmentType
} from 'data/generated/graphql';
import { useElementPosition } from 'hooks';
import { usePublicationSettings } from 'hooks/publication-settings';
import { useIsAdmin, useIsSuper } from 'hooks/roles';
import { AllessehContent } from 'hooks/useAllessehContentQuery';
import { usePrompt } from 'hooks/useBeforeLeavePage';
import useIsNewSectionPage from 'hooks/useIsNewSectionPage';
import { MULTIZONE_MODULE } from 'utils/constants';
import { safelyParseContent } from 'utils/temp';
import { isValidUtc } from 'utils/time';
import { PageDraftSection } from './components/page-draft-section/PageDraftSection';
import { PageMultitabSection, Tabs } from './components/page-multitab-section/PageMultitabSection';
import { PageTitleHeader } from './components/page-title-header/PageTitleHeader';
import Scratchpad from './components/scratch-pad';

const hideIfMultizoneModule = (module: ModuleContainerUnion) => !module.subtype || module.subtype !== MULTIZONE_MODULE;

export const SectionPageEdit = ({ isRefetching = false }: { isRefetching?: boolean }) => {
  const isCreatingNewPage = useIsNewSectionPage();
  const { removableUiModules, isSection } = usePageTypeSettingsContext();
  const { canSave, hasPageChanged } = usePagePublish();
  const isAdmin = useIsAdmin();
  const isSuper = useIsSuper();
  const { data } = usePublicationSettings();
  const pageLockState = usePageLock();
  const { state } = pageLockState;
  const { isReadOnly } = state.publishing;

  const contextActions = useMemo<MenuActions>(
    () => ({
      [MenuActionsKeys.DraftArticle]: [
        {
          showIf: (params) => {
            const validations = [showIfMobileArticle, isAllowedInZone];

            return validations.every((validation) =>
              validation({
                ...params,
                rulesConfig: data?.publicationSetting.rulesConfig
              })
            );
          },
          component: ({ entity, hierarchyId }) => {
            const rulesConfig = data?.publicationSetting.rulesConfig;

            return (
              <MobileArticleActions
                rulesConfig={rulesConfig}
                hierarchyId={hierarchyId!}
                article={entity as MobileArticleItem}
                isReadOnly={isReadOnly}
              />
            );
          },
          withDivider: 'bottom'
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <EditAltSumm content={safelyParseContent<AllessehContent>(article)} viewOnly={isReadOnly} />;
          },
          showIf: () => !isSection
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <OpenInCMS content={safelyParseContent<AllessehContent>(article)} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <OpenInNewsGrid content={safelyParseContent<AllessehContent>(article)} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <ViewPublishedURL content={safelyParseContent<AllessehContent>(article)} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <CopyPublishedURL content={safelyParseContent<AllessehContent>(article)} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            const content = safelyParseContent<AllessehContent>(article);

            return (
              <ExternalLink
                contentId={content.data.id}
                isPublished={!!content.data.attributes.published_datetime}
                type={ExternalLinkContentType.ARTICLE}
                embargo={content.data.attributes.content_status === 'embargo'}
              />
            );
          }
        },
        {
          component: ({ hierarchyId }) => (
            <RemoveEntity hierarchyId={hierarchyId!} text="Remove from module" disabled={isReadOnly} />
          ),
          withDivider: 'top'
        }
      ],
      [MenuActionsKeys.DraftExternalCollection]: [
        {
          component: ({ hierarchyId }) => (
            <RemoveEntity hierarchyId={hierarchyId!} text="Remove from module" disabled={isReadOnly} />
          ),
          withDivider: 'bottom'
        },
        {
          component: ({ entity }) => {
            const collection = entity as ExternalCollectionItem;
            return <EditCollection collection={collection} />;
          },
          showIf: ({ extraProperties: { isExternalCollection } = {}, entity }) => {
            // This is a workaround for collections that are set during Reset to Default, as we do not have access
            // to the correct utc value. It is hard-coded to 0 in `useGetDefaultCollection`.
            const collection = entity as ExternalCollectionItem;
            const hasValidUtc = isValidUtc(collection.metadata.createdUtc);
            return !isExternalCollection && !isSection && hasValidUtc;
          }
        },
        {
          component: ({ hierarchyId, entity }) => {
            const collection = entity as ExternalCollectionItem;
            return <DetachFromCollection hierarchyId={hierarchyId!} collection={collection} disabled={isReadOnly} />;
          },
          showIf: ({ extraProperties: { isExternalCollection } = {} }) => !isExternalCollection && !isSection
        }
      ],
      [MenuActionsKeys.DraftQuery]: [
        {
          component: ({ hierarchyId }) => (
            <RemoveEntity hierarchyId={hierarchyId!} text="Remove from module" disabled={isReadOnly} />
          ),
          withDivider: 'bottom'
        },
        {
          component: ({ hierarchyId, entity, extraProperties }) => {
            const query = entity as QueryItem;
            return (
              <EditQuery
                hierarchyId={hierarchyId!}
                query={query}
                numItemsToShow={extraProperties!.numItemsToShow!}
                baseQuery={extraProperties!.baseQuery!}
                ignoreBaseQuery={extraProperties!.ignoreBaseQuery}
                viewOnly={isReadOnly}
              />
            );
          }
        }
      ],
      [MenuActionsKeys.DraftLinkedItem]: [
        {
          component: ({ hierarchyId }) => {
            if (!hierarchyId) {
              return <></>;
            }

            return <RemoveEntity hierarchyId={hierarchyId} text="Remove from module" disabled={isReadOnly} />;
          },
          withDivider: 'bottom'
        },
        {
          component: ({ entity }) => <EditLinkedItem uuid={(entity as LinkedItem).uuid} />
        },
        {
          component: ({ entity }) => {
            const linkedItem = entity as LinkedItem;
            return <ViewPublishedURL externalLink={linkedItem.attributes.hosted_url} />;
          }
        }
      ],
      [MenuActionsKeys.DraftModule]: [
        {
          component: ({ hierarchyId, entity }) => {
            const module = entity as ModuleContainer;
            return (
              <EditModule
                hierarchyId={hierarchyId!}
                module={module}
                isAdmin={isAdmin || isSuper}
                viewOnly={isReadOnly}
              />
            );
          },
          showIf: ({ entity }) => {
            const module = entity as ModuleContainer;
            const pageModuleAttribute = module.attributes.pageModule as PageModule;
            const moduleTypesEnabled = [
              UiModuleType.UiRankedModuleType,
              UiModuleType.UiTitleModuleType,
              UiModuleType.UiTextModuleType,
              UiModuleType.UiTitlePinnedModuleType
            ];
            return (
              ((isAdmin || isSuper) && !module.attributes.disableModuleIdEdit) ||
              moduleTypesEnabled.some((moduleType) => moduleType === pageModuleAttribute.uiModuleType)
            );
          }
        },
        {
          component: ({ hierarchyId, entity, extraProperties }) => {
            const module = entity as ModuleContainer;

            return (
              <AddQuery
                moduleHierarchyId={hierarchyId}
                module={module}
                hierarchyId={`${hierarchyId}-${module.collection.length}`}
                maxAllowedItems={extraProperties!.maxAllowedItems!}
                numTotalItemsUsed={extraProperties!.numTotalItemsUsed!}
                treatmentType={extraProperties!.treatmentType}
                setOpen={extraProperties!.setOpen}
                ignoreBaseQuery={extraProperties!.ignoreBaseQuery}
                viewOnly={isReadOnly}
              />
            );
          },
          showIf: ({ entity }) => {
            const module = entity as ModuleContainer;
            const pageModuleAttribute = module.attributes.pageModule as PageModule;
            return (
              pageModuleAttribute.uiModuleType !== UiModuleType.UiFeedsModuleType && hideIfMultizoneModule(module)
            );
          }
        },
        {
          component: ({ hierarchyId, extraProperties }) => (
            <AddCollection
              hierarchyId={hierarchyId}
              treatmentType={extraProperties?.treatmentType}
              disabled={isReadOnly}
            />
          ),
          showIf: ({ extraProperties, isHistory, entity }) => {
            const module = entity as ModuleContainer;
            const isOptionalAndEmpty = extraProperties?.isOptionalModule && module.collection.length === 0;

            const isSpecificTreatmentType = [
              UiTitleTreatmentType.SuperTopperTopOpinion,
              UiTitleTreatmentType.RightRailTopOpinion,
              UiTitleTreatmentType.RightRailLowerJr,
              UiTitleTreatmentType.RightRailTopFreeExpression,
              UiTitleTreatmentType.Polymarket
            ].includes(extraProperties?.treatmentType as UiTitleTreatmentType);

            return !isHistory && (isOptionalAndEmpty || isSpecificTreatmentType);
          }
        },
        {
          component: ({ hierarchyId, entity }) => {
            const module = entity as ModuleContainer;
            return <AddLinkedItem hierarchyId={hierarchyId!} module={module} viewOnly={isReadOnly} />;
          },
          showIf: ({ entity }) => {
            const module = entity as ModuleContainer;
            const pageModuleAttribute = module.attributes.pageModule as PageModule;
            return (
              pageModuleAttribute.uiModuleType !== UiModuleType.UiFeedsModuleType && hideIfMultizoneModule(module)
            );
          }
        },
        {
          component: ({ hierarchyId, entity }) => {
            const module = entity as ModuleContainer;
            return <AddMobileLinkedItem hierarchyId={hierarchyId!} module={module} viewOnly={isReadOnly} />;
          },
          showIf: ({ entity }) => {
            const module = entity as ModuleContainer;
            const pageModuleAttribute = module.attributes.pageModule as PageModule;
            return (
              pageModuleAttribute.uiModuleType !== UiModuleType.UiFeedsModuleType && !hideIfMultizoneModule(module)
            );
          }
        },
        {
          component: ({ hierarchyId, entity, extraProperties }) => {
            const module = entity as ModuleContainer;
            return (
              <AddAllessehCollection
                hierarchyId={hierarchyId!}
                module={module}
                maxAllowedItems={extraProperties!.maxAllowedItems!}
                viewOnly={isReadOnly}
              />
            );
          },
          showIf: ({ entity }) => {
            const module = entity as ModuleContainer;
            const pageModuleAttribute = module.attributes.pageModule as PageModule;
            return (
              pageModuleAttribute.uiModuleType !== UiModuleType.UiFeedsModuleType &&
              // !isSection && TODO: revisit this
              hideIfMultizoneModule(module)
            );
          }
        },
        {
          component: ({ hierarchyId, entity, extraProperties }) => {
            const module = entity as ModuleContainer;
            return (
              <ConvertToCollection
                hierarchyId={hierarchyId!}
                module={module}
                maxAllowedItems={extraProperties!.maxAllowedItems!}
                viewOnly={isReadOnly}
              />
            );
          },
          showIf: ({ entity }) => {
            const module = entity as ModuleContainer;
            const pageModuleAttribute = module.attributes.pageModule as PageModule;
            return (
              pageModuleAttribute.uiModuleType !== UiModuleType.UiFeedsModuleType &&
              !isSection &&
              hideIfMultizoneModule(module)
            );
          }
        },
        {
          component: ({ hierarchyId }) => (
            <RemoveEntity
              hierarchyId={hierarchyId!}
              text="Remove module"
              confirmIfNotEmpty
              confirmHint="Note that recent publishes can be viewed in the History tab"
              disabled={isReadOnly}
            />
          ),
          showIf: ({
            entity,
            extraProperties: { canRemoveFromGroup, forceHideRemoveFromGroup, isOptionalModule } = {}
          }) => {
            const module = entity as ModuleContainer;
            const pageModuleAttribute = module.attributes.pageModule as PageModule;
            const removableUiModuleTypes = removableUiModules.map(
              (availableUiModule) => availableUiModule.defaultModule.uiModuleType
            );

            return (
              !forceHideRemoveFromGroup &&
              !isOptionalModule &&
              (canRemoveFromGroup ?? removableUiModuleTypes.includes(pageModuleAttribute.uiModuleType))
            );
          },
          withDivider: 'top'
        }
      ],
      [MenuActionsKeys.HistoryArticle]: [
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <OpenInCMS content={safelyParseContent<AllessehContent>(article)} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <OpenInNewsGrid content={safelyParseContent<AllessehContent>(article)} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <ViewPublishedURL content={safelyParseContent<AllessehContent>(article)} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <CopyPublishedURL content={safelyParseContent<AllessehContent>(article)} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            const content = safelyParseContent<AllessehContent>(article);

            return (
              <ExternalLink
                contentId={content.data.id}
                isPublished={!!content.data.attributes.published_datetime}
                type={ExternalLinkContentType.ARTICLE}
                embargo={content.data.attributes.content_status === 'embargo'}
              />
            );
          }
        }
      ],
      [MenuActionsKeys.HistoryExternalCollection]: [
        {
          component: ({ entity }) => {
            const collection = entity as ExternalCollectionItem;
            return <EditCollection collection={collection} />;
          },
          showIf: ({ extraProperties: { isExternalCollection } = {} }) => !isExternalCollection && !isSection
        }
      ],
      [MenuActionsKeys.HistoryQuery]: [
        {
          component: ({ hierarchyId, entity, extraProperties }) => {
            const query = entity as QueryItem;
            return (
              <EditQuery
                hierarchyId={hierarchyId!}
                query={query}
                numItemsToShow={extraProperties!.numItemsToShow!}
                baseQuery={extraProperties!.baseQuery!}
                ignoreBaseQuery={extraProperties!.ignoreBaseQuery}
                viewOnly
              />
            );
          }
        }
      ],
      [MenuActionsKeys.HistoryLinkedItem]: [
        {
          component: ({ entity }) => {
            const linkedItem = entity as LinkedItem;
            return <ViewPublishedURL externalLink={linkedItem.attributes.hosted_url} />;
          }
        }
      ],
      [MenuActionsKeys.HistoryModule]: [
        {
          component: ({ hierarchyId, entity }) => {
            const module = entity as ModuleContainer;
            return <EditModule hierarchyId={hierarchyId!} module={module} viewOnly isAdmin={isAdmin || isSuper} />;
          },
          showIf: ({ entity }) => {
            const module = entity as ModuleContainer;
            const pageModuleAttribute = module.attributes.pageModule as PageModule;
            const moduleTypesEnabled = [
              UiModuleType.UiRankedModuleType,
              UiModuleType.UiTitleModuleType,
              UiModuleType.UiTextModuleType
            ];
            return moduleTypesEnabled.some((moduleType) => moduleType === pageModuleAttribute.uiModuleType);
          }
        }
      ],
      [MenuActionsKeys.ArticleSearch]: [
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <EditAltSumm content={safelyParseContent<AllessehContent>(article)} viewOnly={isReadOnly} />;
          },
          showIf: () => !isSection
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <OpenInCMS content={safelyParseContent<AllessehContent>(article)} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <OpenInNewsGrid content={safelyParseContent<AllessehContent>(article)} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <ViewPublishedURL content={safelyParseContent<AllessehContent>(article)} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <CopyPublishedURL content={safelyParseContent<AllessehContent>(article)} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            const content = safelyParseContent<AllessehContent>(article);

            return (
              <ExternalLink
                contentId={content.data.id}
                isPublished={!!content.data.attributes.published_datetime}
                type={ExternalLinkContentType.ARTICLE}
                embargo={content.data.attributes.content_status === 'embargo'}
              />
            );
          }
        }
      ],
      [MenuActionsKeys.ArticleQueryModal]: [
        {
          component: ({ extraProperties }) => (
            <ExcludeFromQuery
              jsonQueryStr={extraProperties!.jsonQueryStr!}
              allessehContent={extraProperties!.allessehContent!}
              setJsonQueryStr={extraProperties!.setJsonQueryStr!}
            />
          ),
          showIf: ({ isHistory }) => !isHistory,
          withDivider: 'bottom'
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <OpenInCMS content={safelyParseContent<AllessehContent>(article)} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <OpenInNewsGrid content={safelyParseContent<AllessehContent>(article)} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <ViewPublishedURL content={safelyParseContent<AllessehContent>(article)} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <CopyPublishedURL content={safelyParseContent<AllessehContent>(article)} />;
          }
        }
      ]
    }),
    [removableUiModules, isAdmin, isSuper, isSection, data?.publicationSetting.rulesConfig, isReadOnly]
  );

  const disablePrompt = process.env.REACT_APP_DISABLE_PROMPT === 'true';

  usePrompt(
    'You have unsaved changes. Are you sure you want to leave?',
    !disablePrompt && canSave && !isCreatingNewPage && hasPageChanged
  );

  const ref = useRef<HTMLDivElement>(null);

  const [currentTab, setCurrentTab] = useState(Tabs.ContentSearch);

  const isNewPage = useIsNewSectionPage();

  const { width, x } = useElementPosition(ref);

  return (
    <PageRootContainer noBottomMargin>
      <ContextMenuActionsProvider actions={contextActions}>
        <WidePageWrapper padding={{ all: 'md' }}>
          <WidePageWrapper.Header>
            <PageTitleHeader data-testid="page-home-title" />
          </WidePageWrapper.Header>
          <WidePageWrapper.Left>
            {isRefetching ? <ResponsiveLoader /> : <PageDraftSection />}
          </WidePageWrapper.Left>
          <WidePageWrapper.Right>
            <div ref={ref}>
              <PageMultitabSection onTabChange={setCurrentTab} />
            </div>
            {!isNewPage &&
              currentTab === Tabs.ContentSearch &&
              localStorage.getItem('scratchpadEnabled') === 'true' && (
                <Scratchpad width={width} x={x} isRefetching={isRefetching} />
              )}
          </WidePageWrapper.Right>
        </WidePageWrapper>
      </ContextMenuActionsProvider>
    </PageRootContainer>
  );
};
